import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import RequirePermission from "../../../auth/RequirePermission";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import AdminTableContainer from "../../../components/midpoint/admin/layout/AdminTableContainer";
import {
  fetchAdminDealerVerifications,
  runAdminDealerAction,
  trackDealerEvent,
} from "../../../services/automapDealerOnboardingApi";
import { formatDate, formatDealerStatus } from "../../../constants/automapDealerOnboardingConstants";
import { AdminDealerNav, statusBadgeClass } from "./dealerAdminShared";

function AdminDealerVerificationsPageContent() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await fetchAdminDealerVerifications();
      setDealers(data.dealers || []);
    } catch (err) {
      setErrorMessage(err.message || "No se pudo cargar la cola de verificación.");
      setDealers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (dealerId, action) => {
    if (!canManage) return;
    try {
      await runAdminDealerAction(dealerId, action);
      trackDealerEvent("dealer_verification_status_changed", { dealerId, action });
      await load();
    } catch (err) {
      setErrorMessage(err.message || "Acción fallida.");
    }
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Concesionarios"
        title="Cola de verificación"
        description="Revisa concesionarios pendientes de verificación."
        navigation={<AdminDealerNav active="verifications" />}
      />
      <main className="admin-console__main">
        {errorMessage ? <AdminStatePanel tone="danger" title="Error" message={errorMessage} /> : null}
        {loading ? (
          <AdminStatePanel tone="info" title="Cargando" message="Obteniendo verificaciones…" />
        ) : dealers.length === 0 ? (
          <AdminStatePanel tone="muted" title="Cola vacía" message="No hay verificaciones pendientes." />
        ) : (
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Concesionario</th>
                  <th>Ciudad</th>
                  <th>Dirección</th>
                  <th>Contacto</th>
                  <th>WhatsApp</th>
                  <th>Web / social</th>
                  <th>Enviado</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {dealers.map((dealer) => {
                  const contact = dealer.contacts?.find((c) => c.isPrimary) || dealer.contacts?.[0];
                  return (
                    <tr key={dealer.id}>
                      <td>
                        <Link to={`/admin/dealers/${dealer.id}`}>{dealer.businessName}</Link>
                      </td>
                      <td>{dealer.city || "—"}</td>
                      <td>{dealer.address || "—"}</td>
                      <td>{contact?.name || "—"}</td>
                      <td>{dealer.mainWhatsapp || "—"}</td>
                      <td>
                        {[dealer.websiteUrl, dealer.instagramUrl].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td>{formatDate(dealer.createdAt)}</td>
                      <td>
                        <span className={`admin-badge ${statusBadgeClass(dealer.verificationStatus)}`}>
                          {formatDealerStatus(dealer.verificationStatus)}
                        </span>
                      </td>
                      <td className="admin-table__actions">
                        {canManage ? (
                          <>
                            <button type="button" onClick={() => runAction(dealer.id, "mark_verified")}>
                              Verificar
                            </button>
                            <button type="button" onClick={() => runAction(dealer.id, "mark_pending_verification")}>
                              Pedir info
                            </button>
                            <button type="button" onClick={() => runAction(dealer.id, "reject_verification")}>
                              Rechazar
                            </button>
                            <button type="button" onClick={() => runAction(dealer.id, "suspend")}>
                              Suspender
                            </button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </AdminTableContainer>
        )}
      </main>
    </AdminPageLayout>
  );
}

export default function AdminDealerVerificationsPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminDealerVerificationsPageContent />
    </RequirePermission>
  );
}
